import React, { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress, Chip } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import moment from "moment";
import { get_session_agenda_api } from "../../../DAL/Tutors/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, infoBlue, filledButtonSx, ACTION_COLORS,
  surface, surfaceRaised, shadow,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * EVERYTHING ABOUT A SESSION THAT HAS NOT HAPPENED YET.
 *
 * WHAT THIS REPLACES
 * A name, a time, a price, and a sentence promising that the recording would
 * appear later. The one genuinely useful thing before a session — the brief —
 * sat behind a button that opened a SECOND dialog on top of this one. A page
 * about a session that you have to leave in order to learn anything about the
 * session is a caption, not a page.
 *
 * THE BRIEF IS ON THE PAGE, NOT BEHIND A BUTTON. It is the reason an
 * instructor does not walk in cold, and a click is enough friction to stop it
 * being read at all when the session starts in four minutes.
 *
 * ORDERED BY WHEN IT IS NEEDED, NOT BY WHERE IT COMES FROM.
 *   1. can I get in, and when          — the only thing that matters at T-2min
 *   2. what should I cover             — the brief, and what they asked for
 *   3. what does it unlock             — the gate; on this course the whole
 *                                        point of the session
 *   4. what do I need to know first    — money, and whether it has moved
 *                                        before
 * Money is fourth deliberately. It is never the question at the top of an
 * instructor's mind before teaching, and putting a price where the brief
 * should be is how a teaching tool starts feeling like an invoice.
 *
 * EVERY BLOCK IS ABSENT WHEN IT HAS NOTHING TO SAY. A page of empty labelled
 * boxes reads as broken; the same page with three blocks reads as finished.
 */

const H = { fontFamily: font };
const label = {
  ...H, fontSize: "10px", fontWeight: 800, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textMuted,
};

const money = (n, c) =>
  c ? new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0) : `${n}`;

/**
 * A card with a heading strip rather than a heading floating inside it.
 *
 * Every block on this page was white on a white page, told apart by a
 * one-pixel line — so nothing read as a card, the headings had no anchor, and
 * the whole screen was one undifferentiated sheet. The strip is `surfaceRaised`
 * and the body is `surface`: two planes and a shadow, which is enough for a
 * card to look like a card without shouting.
 */
function Block({ label: l, children, tone }) {
  const kids = React.Children.toArray(children).filter(Boolean);
  if (!kids.length) return null;
  return (
    <Box sx={{
      mb: 1.75, borderRadius: "12px", overflow: "hidden", backgroundColor: surface,
      border: `1px solid ${border}`, boxShadow: shadow,
      ...(tone ? { borderLeft: `3px solid ${tone}` } : {}),
    }}>
      <Box sx={{
        backgroundColor: surfaceRaised, borderBottom: `1px solid ${border}`,
        px: 1.5, py: 0.75,
      }}>
        <Typography sx={{ ...label, color: tone || textSecondary, m: 0 }}>{l}</Typography>
      </Box>
      <Box sx={{ px: 1.5, py: 1.25 }}>{kids}</Box>
    </Box>
  );
}

function Fact({ k, v, tone }) {
  if (v === null || v === undefined || v === "") return null;
  return (
    <Box sx={{ display: "flex", gap: 1, py: 0.35, alignItems: "baseline" }}>
      <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, minWidth: 116, flexShrink: 0 }}>{k}</Typography>
      <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 600, color: tone || textPrimary }}>{v}</Typography>
    </Box>
  );
}

const GATE_STATE = {
  studying: "still working through it",
  awaiting_session: "waiting on a session with you",
  session_booked: "this session",
  signed_off: "signed off",
  locked: "locked",
};

export default function SessionUpcoming({ session, gate, consent = [], onJoin }) {
  const [agenda, setAgenda] = useState({ loading: true, data: null });

  useEffect(() => {
    let dead = false;
    if (!session?.id || session.kind === "class") { setAgenda({ loading: false, data: null }); return undefined; }
    get_session_agenda_api(session.id).then((r) => {
      if (!dead) setAgenda({ loading: false, data: r?.code === 200 ? (r.data || null) : null });
    });
    return () => { dead = true; };
  }, [session?.id, session?.kind]);

  if (!session) return null;
  const a = agenda.data;
  const j = session.join || {};
  /**
   * Identical work, set more than once, collapses to one line with a count.
   *
   * The same fix as RecordWork.jsx, because this page reads the same tasks: a
   * student carrying four copies of "Start their first subject" filled the
   * brief with the same sentence four times, so the one piece of work that
   * actually mattered was the fifth line down.
   *
   * Keyed on title plus due date, so the same title genuinely set for two
   * different weeks stays two lines.
   */
  const owed = (() => {
    const out = [];
    const byKey = new Map();
    for (const t of a?.openTasks || []) {
      const key = `${(t.title || "").trim().toLowerCase()}|${t.dueAt || ""}`;
      const seen = byKey.get(key);
      if (seen) { seen.count += 1; continue; }
      const entry = { task: t, count: 1 };
      byKey.set(key, entry);
      out.push(entry);
    }
    return out;
  })();

  const p = session.payment || {};
  const h = session.history || {};
  const startsIn = moment(session.startedAt).fromNow();

  return (
    <Box>
      {/* ── 1. can I get in ──────────────────────────────────────────── */}
      {j.applicable && (
        <Block label="The room" tone={j.open ? success : undefined}>
          {j.open ? (
            <>
              <Typography sx={{ ...H, fontSize: "13px", fontWeight: 700, color: success, mb: 0.9 }}>
                The room is open.
              </Typography>
              <Button
                onClick={() => onJoin?.(session)}
                startIcon={<VideocamIcon sx={{ fontSize: 16 }} />}
                sx={{ ...filledButtonSx(ACTION_COLORS.join), py: 0.7, px: 1.8, fontSize: "13px" }}
              >
                Join now
              </Button>
            </>
          ) : j.state === "ended" ? (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
              The room has closed.
            </Typography>
          ) : (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, lineHeight: 1.6 }}>
              Opens {moment(j.opensAt).format("HH:mm")}, ten minutes before the start. Starts {startsIn}.
            </Typography>
          )}
        </Block>
      )}

      {/* ── 2. what should I cover ───────────────────────────────────── */}
      {session.studentNote && (
        <Block label="What they asked for" tone={infoBlue}>
          <Typography sx={{ ...H, fontSize: "13px", color: textPrimary, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {session.studentNote}
          </Typography>
          <Typography sx={{ ...H, fontSize: "11px", color: textMuted, mt: 0.7 }}>
            {/* Their own words outrank any ranking, and are said so. */}
            The student wrote this when booking. It is the one thing no amount of data infers.
          </Typography>
        </Block>
      )}

      {session.kind !== "class" && (
        <Block label="Where they are weakest">
          {agenda.loading ? (
            <CircularProgress size={15} sx={{ color: accent }} />
          ) : !a ? (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
              No brief yet. It is built from their practice, and there is not enough of it to rank.
              Ask them where they feel shaky.
            </Typography>
          ) : (
            <>
              {/* The confidence sentence is as prominent as the list. An
                  instructor told a ranking is thin will ask the student; one
                  handed a confident-looking list will not. */}
              <Typography sx={{
                ...H, fontSize: "11.5px", lineHeight: 1.5, mb: 1,
                color: a.confidence === "low" ? warning : textSecondary,
                fontWeight: a.confidence === "low" ? 600 : 400,
              }}>
                {a.basis}
              </Typography>
              {(a.focus || []).length ? (a.focus || []).slice(0, 5).map((f, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.4, borderTop: i ? `1px solid ${border}` : "none" }}>
                  <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 600, color: textPrimary, flex: 1, minWidth: 0 }}>
                    {String(f.topic || "").replace(/-/g, " ")}
                  </Typography>
                  {(f.acsCodes || []).slice(0, 3).map((c) => (
                    <Chip key={c} label={c} size="small" sx={{ ...H, fontSize: "10px", height: 18, backgroundColor: `${accent}12`, color: accent }} />
                  ))}
                  <Typography sx={{ ...H, fontSize: "11.5px", color: warning, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                    {f.wrong}/{f.attempts} wrong
                  </Typography>
                </Box>
              )) : (
                <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
                  Nothing stands out yet.
                </Typography>
              )}
            </>
          )}
        </Block>
      )}

      {a?.lastSession && (
        <Block label="Last time">
          <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mb: 0.4 }}>
            {a.lastSession.at ? moment(a.lastSession.at).format("D MMM YYYY") : "Previously"}
          </Typography>
          <Typography sx={{ ...H, fontSize: "13px", color: textPrimary, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {a.lastSession.summary || "No debrief was written for that session."}
          </Typography>
        </Block>
      )}

      {owed.length > 0 && (
        <Block label="What they still owe">
          {owed.map(({ task: t, count }, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.35 }}>
              <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, flex: 1, minWidth: 0 }}>{t.title}</Typography>
              {count > 1 && (
                <Typography sx={{ ...H, fontSize: "11px", fontWeight: 700, color: textSecondary, flexShrink: 0 }}>
                  &times;{count}
                </Typography>
              )}
              {t.dueAt && (
                <Typography sx={{
                  ...H, fontSize: "11.5px", flexShrink: 0,
                  color: new Date(t.dueAt) < new Date() ? warning : textSecondary,
                }}>
                  {moment(t.dueAt).fromNow()}
                </Typography>
              )}
            </Box>
          ))}
        </Block>
      )}

      {/* ── 3. what does it unlock ───────────────────────────────────── */}
      {gate && !gate.inactive && session.kind !== "class" && (
        <Block label="Where this sits in the course">
          <Fact k="They are on" v={`Lesson ${gate.currentLesson}: ${GATE_STATE[gate.currentState] || gate.currentState}`} />
          <Fact k="Signed off" v={`${gate.lessonsSignedOff} lesson${gate.lessonsSignedOff === 1 ? "" : "s"}`} />
          {gate.currentState === "session_booked" && (
            <Typography sx={{ ...H, fontSize: "12px", color: accent, fontWeight: 600, mt: 0.6, lineHeight: 1.55 }}>
              {/* The reason the session exists, on the page about the session. */}
              This is the consolidation session for Lesson {gate.currentLesson}. Writing the debrief
              afterwards is what opens Lesson {gate.currentLesson + 1} for them.
            </Typography>
          )}
        </Block>
      )}

      {/* ── 4. what should I know first ──────────────────────────────── */}
      <Block label="Booking">
        <Fact k="Length" v={`${session.minutes} minutes`} />
        <Fact
          k="Payment"
          v={p.held
            ? `${money(p.price, session.currency)} held, taken after the session`
            : p.status === "paid"
              ? `${money(p.price, session.currency)} paid`
              : p.status || "\u2013"}
          tone={p.status === "failed" ? warning : undefined}
        />
        {h.rescheduled > 0 && (
          <Fact
            k="Moved"
            v={`${h.rescheduled} time${h.rescheduled === 1 ? "" : "s"}${h.lastMove?.reason ? `, "${h.lastMove.reason}"` : ""}`}
            tone={warning}
          />
        )}
        {h.handovers > 0 && (
          <Fact
            k="Handed over"
            v={`from ${h.lastHandover?.fromName || "another instructor"}${h.lastHandover?.reason ? `, "${h.lastHandover.reason}"` : ""}`}
            tone={warning}
          />
        )}
        {h.topicChanged && <Fact k="Topic" v="Changed since booking" tone={warning} />}
        {consent.length > 0 && (
          <Fact
            k="Recording"
            v={consent.every((c) => c.granted)
              ? "Both agreed"
              : consent.some((c) => !c.granted)
                ? "Someone declined, it will not record"
                : "Waiting on consent"}
            tone={consent.some((c) => !c.granted) ? textSecondary : undefined}
          />
        )}
      </Block>
    </Box>
  );
}
